import React, { useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { fetchProjects } from '../services/projectService';
import DepartmentService from '../../layers/departments/DepartmentService';
import IndigenousReserveService from '../../layers/indigenous-reserves/IndigenousReserveService';
import CommunityCouncilService from '../../layers/community-councils/CommunityCouncilService';
import TerritorioCampesinoAgroalimentarioService from '../../layers/territorios-campesinos-agroalimentarios/TerritorioCampesinoAgroalimentarioService';
import RunapService from '../../layers/runap/RunapService';
import TituloVigenteService from '../../layers/titulo-vigente/TituloVigenteService';
import ZonaReservaCampesinaService from '../../layers/zona-reserva-campesina/ZonaReservaCampesinaService';
import IdeamDeforestationService from '../../layers/ideam-deforestation/IdeamDeforestationService';
import ProjectDetailsPanel from './ProjectDetailsPanel';
import { Dropdown } from 'primereact/dropdown';
import { Card } from 'primereact/card';
import { Checkbox } from 'primereact/checkbox';

// Module-level flag to prevent duplicate API calls
let projectsApiCalled = false;

const MapComponent = () => {
  const currentCallId = useRef(0);
  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const [allProjects, setAllProjects] = useState([]);
  const [departmentsGeoJson, setDepartmentsGeoJson] = useState(null);
  const [indigenousReservesGeoJson, setIndigenousReservesGeoJson] = useState(null);
  const [communityCouncilsGeoJson, setCommunityCouncilsGeoJson] = useState(null);
  const [territoriosCampesinosAgroalimentariosGeoJson, setTerritoriosCampesinosAgroalimentariosGeoJson] = useState(null);
  const [runapGeoJson, setRunapGeoJson] = useState(null);
  const [tituloVigenteGeoJson, setTituloVigenteGeoJson] = useState(null);
  const [zonaReservaCampesinaGeoJson, setZonaReservaCampesinaGeoJson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [filters, setFilters] = useState({
    departamento: null,
    status: null,
    certifier: null,
    name: null
  });
  const [departamentos, setDepartamentos] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [certifiers, setCertifiers] = useState([]);
  const [projectNames, setProjectNames] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [layerVisibility, setLayerVisibility] = useState({
    departments: false,
    indigenousReserves: false,
    communityCouncils: false,
    territoriosCampesinosAgroalimentarios: false,
    runap: false,
    tituloVigente: false,
    zonaReservaCampesina: false,
    ideamDeforestation: false
  });
  const [layerLoading, setLayerLoading] = useState(null); // Track which layer is loading
  const [isLayersPanelExpanded, setIsLayersPanelExpanded] = useState(true);
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(false);
  const [filteredProjectCount, setFilteredProjectCount] = useState(0);

  useEffect(() => {
    const loadProjects = async () => {
      try {
        console.log('Fetching projects...');
        const data = await fetchProjects();
        console.log('Projects fetched:', data);
        setAllProjects(data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching projects:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    loadProjects();
  }, []);

  useEffect(() => {
    if (allProjects.length > 0) {
      // Split comma-separated departments and create unique list
      const uniqueDepartamentos = [...new Set(
        allProjects
          .map(p => p.departamento)
          .filter(d => d)
          .flatMap(d => d.split(',').map(dep => dep.trim()))
      )];
      const uniqueStatuses = [...new Set(allProjects.map(p => p.status?.name).filter(s => s))];
      const uniqueCertifiers = [...new Set(allProjects.map(p => p.certifier?.name).filter(c => c))];
      const uniqueNames = [...new Set(allProjects.map(p => p.name).filter(n => n))];

      // Create options arrays for Dropdowns (sorted alphabetically)
      const departamentoOptions = uniqueDepartamentos.sort((a, b) => a.localeCompare(b)).map(dep => ({ label: dep, value: dep }));
      const statusOptions = uniqueStatuses.sort((a, b) => a.localeCompare(b)).map(stat => ({ label: stat, value: stat }));
      const certifierOptions = uniqueCertifiers.sort((a, b) => a.localeCompare(b)).map(cert => ({ label: cert, value: cert }));
      const nameOptions = uniqueNames.sort((a, b) => a.localeCompare(b)).map(name => ({ label: name, value: name }));

      setDepartamentos(departamentoOptions);
      setStatuses(statusOptions);
      setCertifiers(certifierOptions);
      setProjectNames(nameOptions);
    }
  }, [allProjects]);

  // Update filtered project count when filters or projects change
  useEffect(() => {
    const filteredCount = allProjects.filter(project => {
      if (filters.departamento && project.departamento !== filters.departamento) return false;
      if (filters.status && project.status?.name !== filters.status) return false;
      if (filters.certifier && project.certifier?.name !== filters.certifier) return false;
      if (filters.name && project.name !== filters.name) return false;
      return true;
    }).length;
    setFilteredProjectCount(filteredCount);
  }, [filters, allProjects]);

  // Helper function to clear all popups and highlights
  const clearAllPopupsAndHighlights = () => {
    const map = mapRef.current;
    if (!map) return;
    
    // Remove all popups
    const popups = document.querySelectorAll('.maplibregl-popup');
    popups.forEach(popup => popup.remove());
    
    // Clear highlight layers if they exist
    if (map.getLayer('projects-highlight')) {
      map.setFilter('projects-highlight', ['==', 'id', '']);
    }
    if (map.getLayer('indigenous-reserves-highlight')) {
      map.setFilter('indigenous-reserves-highlight', ['==', 'id', '']);
    }
    if (map.getLayer('community-councils-highlight')) {
      map.setFilter('community-councils-highlight', ['==', 'id', '']);
    }
    if (map.getLayer('territorios-campesinos-agroalimentarios-highlight')) {
      map.setFilter('territorios-campesinos-agroalimentarios-highlight', ['==', 'id', '']);
    }
    if (map.getLayer('runap-highlight')) {
      map.setFilter('runap-highlight', ['==', 'id', '']);
    }
    if (map.getLayer('titulo-vigente-highlight')) {
      map.setFilter('titulo-vigente-highlight', ['==', 'id', '']);
    }
    if (map.getLayer('zona-reserva-campesina-highlight')) {
      map.setFilter('zona-reserva-campesina-highlight', ['==', 'id', '']);
    }
  };

  // Lazy loading handlers for layers
  const handleDepartmentsLayerToggle = async (checked) => {
    setLayerVisibility(prev => ({ ...prev, departments: checked }));
    if (!checked) {
      clearAllPopupsAndHighlights();
    }
    if (checked && mapRef.current) {
      try {
        console.log('Adding departments as vector tiles...');
        addDepartmentsToMap(mapRef.current);
      } catch (err) {
        console.error('Error adding departments layer:', err);
      }
    }
  };

  const handleIndigenousReservesLayerToggle = async (checked) => {
    setLayerVisibility(prev => ({ ...prev, indigenousReserves: checked }));
    if (!checked) {
      clearAllPopupsAndHighlights();
    }
    if (checked && mapRef.current) {
      try {
        console.log('Adding indigenous reserves as vector tiles...');
        addIndigenousReservesToMap(mapRef.current);
      } catch (err) {
        console.error('Error adding indigenous reserves layer:', err);
      }
    }
  };

  const handleCommunityCouncilsLayerToggle = async (checked) => {
    setLayerVisibility(prev => ({ ...prev, communityCouncils: checked }));
    if (!checked) {
      clearAllPopupsAndHighlights();
    }
    if (checked && mapRef.current) {
      try {
        console.log('Adding community councils as vector tiles...');
        addCommunityCouncilsToMap(mapRef.current);
      } catch (err) {
        console.error('Error adding community councils layer:', err);
      }
    }
  };

  const handleTerritoriosCampesinosAgroalimentariosLayerToggle = async (checked) => {
    setLayerVisibility(prev => ({ ...prev, territoriosCampesinosAgroalimentarios: checked }));
    if (!checked) {
      clearAllPopupsAndHighlights();
    }
    if (checked && mapRef.current) {
      try {
        console.log('Adding territorios campesinos agroalimentarios as vector tiles...');
        addTerritoriosCampesinosAgroalimentariosToMap(mapRef.current);
      } catch (err) {
        console.error('Error adding territorios campesinos agroalimentarios layer:', err);
      }
    }
  };

  const handleRunapLayerToggle = async (checked) => {
    setLayerVisibility(prev => ({ ...prev, runap: checked }));
    if (!checked) {
      clearAllPopupsAndHighlights();
    }
    if (checked && mapRef.current) {
      try {
        console.log('Adding RUNAP areas as vector tiles...');
        addRunapToMap(mapRef.current);
      } catch (err) {
        console.error('Error adding RUNAP areas layer:', err);
      }
    }
  };

  const handleTituloVigenteLayerToggle = async (checked) => {
    setLayerVisibility(prev => ({ ...prev, tituloVigente: checked }));
    if (!checked) {
      clearAllPopupsAndHighlights();
    }
    if (checked && mapRef.current) {
      try {
        console.log('Adding Titulos Vigentes as vector tiles...');
        addTituloVigenteToMap(mapRef.current);
      } catch (err) {
        console.error('Error adding Titulos Vigentes layer:', err);
      }
    }
  };

  const handleZonaReservaCampesinaLayerToggle = async (checked) => {
    setLayerVisibility(prev => ({ ...prev, zonaReservaCampesina: checked }));
    if (!checked) {
      clearAllPopupsAndHighlights();
    }
    if (checked && mapRef.current) {
      try {
        console.log('Adding Zonas de Reserva Campesina as vector tiles...');
        addZonaReservaCampesinaToMap(mapRef.current);
      } catch (err) {
        console.error('Error adding Zonas de Reserva Campesina layer:', err);
      }
    }
  };

  const handleIdeamDeforestationLayerToggle = (checked) => {
    setLayerVisibility(prev => ({ ...prev, ideamDeforestation: checked }));
    if (!checked) {
      clearAllPopupsAndHighlights();
    }
    const map = mapRef.current;
    if (!map) return;
    
    if (checked) {
      try {
        console.log('Adding IDEAM Deforestation WMS layer...');
        addIdeamDeforestationToMap(map);
      } catch (err) {
        console.error('Error adding IDEAM Deforestation layer:', err);
      }
    } else {
      if (map.getLayer('ideam-deforestation-fill')) {
        map.removeLayer('ideam-deforestation-fill');
      }
      if (map.getSource('ideam-deforestation')) {
        map.removeSource('ideam-deforestation');
      }
      console.log('IDEAM Deforestation layer removed');
    }
  };

  useEffect(() => {
    if (mapRef.current && mapLoaded) {
      addProjectsToMap(mapRef.current, allProjects);
    }
  }, [mapLoaded]);

  useEffect(() => {
    if (mapRef.current && allProjects.length > 0 && mapRef.current.getLayer('projects-fill')) {
      applyFiltersToMap();
    }
  }, [filters, mapLoaded, allProjects]);

  useEffect(() => {
    if (mapRef.current && mapLoaded) {
      const map = mapRef.current;
      if (map.getLayer('departments-borders')) {
        map.setLayoutProperty('departments-borders', 'visibility', layerVisibility.departments ? 'visible' : 'none');
      }
      if (map.getLayer('indigenous-reserves-fill')) {
        map.setLayoutProperty('indigenous-reserves-fill', 'visibility', layerVisibility.indigenousReserves ? 'visible' : 'none');
        map.setLayoutProperty('indigenous-reserves-borders', 'visibility', layerVisibility.indigenousReserves ? 'visible' : 'none');
      }
      if (map.getLayer('community-councils-fill')) {
        map.setLayoutProperty('community-councils-fill', 'visibility', layerVisibility.communityCouncils ? 'visible' : 'none');
        map.setLayoutProperty('community-councils-borders', 'visibility', layerVisibility.communityCouncils ? 'visible' : 'none');
      }
      if (map.getLayer('territorios-campesinos-agroalimentarios-fill')) {
        map.setLayoutProperty('territorios-campesinos-agroalimentarios-fill', 'visibility', layerVisibility.territoriosCampesinosAgroalimentarios ? 'visible' : 'none');
        map.setLayoutProperty('territorios-campesinos-agroalimentarios-borders', 'visibility', layerVisibility.territoriosCampesinosAgroalimentarios ? 'visible' : 'none');
      }
      if (map.getLayer('runap-fill')) {
        map.setLayoutProperty('runap-fill', 'visibility', layerVisibility.runap ? 'visible' : 'none');
        map.setLayoutProperty('runap-borders', 'visibility', layerVisibility.runap ? 'visible' : 'none');
      }
      if (map.getLayer('titulo-vigente-fill')) {
        map.setLayoutProperty('titulo-vigente-fill', 'visibility', layerVisibility.tituloVigente ? 'visible' : 'none');
        map.setLayoutProperty('titulo-vigente-borders', 'visibility', layerVisibility.tituloVigente ? 'visible' : 'none');
      }
      if (map.getLayer('zona-reserva-campesina-fill')) {
        map.setLayoutProperty('zona-reserva-campesina-fill', 'visibility', layerVisibility.zonaReservaCampesina ? 'visible' : 'none');
        map.setLayoutProperty('zona-reserva-campesina-borders', 'visibility', layerVisibility.zonaReservaCampesina ? 'visible' : 'none');
      }
    }
  }, [layerVisibility, mapLoaded]);

  // Resize map when panel opens/closes and clear highlight when project is deselected
  useEffect(() => {
    if (mapRef.current) {
      const map = mapRef.current;
      
      // Clear highlight when selectedProject is null (panel closed or clicked away)
      if (!selectedProject && map.getLayer('projects-highlight')) {
        map.removeLayer('projects-highlight');
      }
      
      setTimeout(() => {
        map.resize();
      }, 300); // Wait for transition to complete
    }
  }, [selectedProject]);

  // Clear highlight when clicking on empty map area
  useEffect(() => {
    if (!mapRef.current || !mapLoaded) return;
    
    const map = mapRef.current;
    
    const handleMapClick = (e) => {
      // Check if click was on a feature
      const features = map.queryRenderedFeatures(e.point);
      const hasFeature = features.length > 0;
      
      // If no feature was clicked and there's a highlight, remove it
      if (!hasFeature && map.getLayer('projects-highlight')) {
        map.removeLayer('projects-highlight');
      }
    };
    
    map.on('click', handleMapClick);
    
    return () => {
      map.off('click', handleMapClick);
    };
  }, [mapLoaded]);

  const addProjectsToMap = (map, projects) => {
    console.log('Adding projects as vector tiles');
    
    // Remove existing source if present
    if (map.getSource('projects')) {
      if (map.getLayer('projects-highlight')) {
        map.removeLayer('projects-highlight');
      }
      map.removeLayer('projects-fill');
      map.removeLayer('projects-borders');
      map.removeSource('projects');
    }

    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api';
    const mvtUrlTemplate = `${apiBaseUrl}/mvt/projects/{z}/{x}/{y}`;
    console.log('MVT URL Template:', mvtUrlTemplate);
    
    // Add vector tile source with custom tile load function
    map.addSource('projects', {
      type: 'vector',
      tiles: [mvtUrlTemplate],
      minzoom: 0,
      maxzoom: 22
    });

    // Add a brown hatched pattern for projects
    const patternId = 'brown-hatch';
    
    // Create SVG pattern for hatching (vertical lines)
    const svgPattern = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12">
      <defs>
        <pattern id="hatch" width="12" height="12" patternUnits="userSpaceOnUse">
          <line x1="3" y1="0" x2="3" y2="12" stroke="#5D2906" stroke-width="2" />
          <line x1="9" y1="0" x2="9" y2="12" stroke="#5D2906" stroke-width="2" />
        </pattern>
      </defs>
      <rect width="12" height="12" fill="white" opacity="0.15"/>
      <rect width="12" height="12" fill="url(#hatch)" />
    </svg>`;
    
    const svgBlob = new Blob([svgPattern], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(svgBlob);
    
    const patternImage = new Image(12, 12);
    patternImage.onload = () => {
      if (!map.hasImage(patternId)) {
        map.addImage(patternId, patternImage);
      }
    };
    patternImage.src = url;

    // Add fill layer for projects with hatched pattern
    map.addLayer({
      id: 'projects-fill',
      type: 'fill',
      source: 'projects',
      'source-layer': 'projects',
      paint: {
        'fill-pattern': patternId,
        'fill-opacity': 1
      }
    });

    // Add border layer for projects
    map.addLayer({
      id: 'projects-borders',
      type: 'line',
      source: 'projects',
      'source-layer': 'projects',
      paint: {
        'line-color': '#000000',
        'line-width': 1
      }
    });

    // Add click event to highlight selected project
    map.on('click', 'projects-fill', (e) => {
      const features = map.queryRenderedFeatures(e.point, {
        layers: ['projects-fill']
      });

      if (features.length > 0) {
        const feature = features[0];
        const projectId = feature.properties.id;

        // Remove existing highlight
        if (map.getLayer('projects-highlight')) {
          map.removeLayer('projects-highlight');
        }

        // Add highlight layer
        map.addLayer({
          id: 'projects-highlight',
          type: 'line',
          source: 'projects',
          'source-layer': 'projects',
          filter: ['==', 'id', projectId],
          paint: {
            'line-color': '#FFFFFF',
            'line-width': 4,
            'line-dasharray': [2, 1]
          }
        });
      }
    });

    // Change cursor on hover
    map.on('mouseenter', 'projects-fill', () => {
      map.getCanvas().style.cursor = 'pointer';
    });

    map.on('mouseleave', 'projects-fill', () => {
      map.getCanvas().style.cursor = '';
    });

    // Log source loading events
    map.on('source.load', 'projects', () => {
      console.log('✅ Projects source loaded successfully');
      const source = map.getSource('projects');
      console.log('Source type:', source.type);
      console.log('Source tiles:', source.tiles);
    });

    map.on('source.error', 'projects', (e) => {
      console.error('❌ Source error:', e.error);
    });

    map.on('layer.error', 'projects-fill', (e) => {
      console.error('❌ Layer error:', e.error);
    });

    // Log data loading events
    map.on('data', (e) => {
      if (e.source === 'projects') {
        console.log('📊 Data event for projects:', e.dataType, '- isSourceLoaded:', e.isSourceLoaded);
      }
    });
    
    console.log('Projects vector tiles added to map');
  };

  const addDepartmentsToMap = (map) => {
    console.log('Adding departments as vector tiles');
    
    // Remove existing source if present
    if (map.getSource('departments')) {
      map.removeLayer('departments-fill');
      map.removeLayer('departments-borders');
      map.removeSource('departments');
    }

    const mvtUrlTemplate = DepartmentService.getMvtUrlTemplate();
    console.log('Departments MVT URL Template:', mvtUrlTemplate);
    
    // Add vector tile source
    map.addSource('departments', {
      type: 'vector',
      tiles: [mvtUrlTemplate],
      minzoom: 0,
      maxzoom: 18
    });

    // Add fill layer for departments (transparent by default)
    map.addLayer({
      id: 'departments-fill',
      type: 'fill',
      source: 'departments',
      'source-layer': 'departments',
      paint: {
        'fill-color': '#e0e0e0',
        'fill-opacity': 0
      }
    });

    // Add border layer for departments
    map.addLayer({
      id: 'departments-borders',
      type: 'line',
      source: 'departments',
      'source-layer': 'departments',
      paint: {
        'line-color': '#666666',
        'line-width': 2
      }
    });

    // Log source loading events
    map.on('source.load', 'departments', () => {
      console.log('✅ Departments source loaded successfully');
      const source = map.getSource('departments');
      console.log('Source type:', source.type);
      console.log('Source tiles:', source.tiles);
    });

    map.on('source.error', 'departments', (e) => {
      console.error('❌ Departments source error:', e.error);
    });
    
    console.log('Departments vector tiles added to map');
  };

  const addIndigenousReservesToMap = (map) => {
    console.log('Adding indigenous reserves as vector tiles');
    
    // Remove existing source if present
    if (map.getSource('indigenous-reserves')) {
      if (map.getLayer('indigenous-reserves-highlight')) {
        map.removeLayer('indigenous-reserves-highlight');
      }
      map.removeLayer('indigenous-reserves-fill');
      map.removeLayer('indigenous-reserves-borders');
      map.removeSource('indigenous-reserves');
    }

    const mvtUrlTemplate = IndigenousReserveService.getMvtUrlTemplate();
    console.log('Indigenous Reserves MVT URL Template:', mvtUrlTemplate);
    
    // Add vector tile source
    map.addSource('indigenous-reserves', {
      type: 'vector',
      tiles: [mvtUrlTemplate],
      minzoom: 0,
      maxzoom: 18
    });

    // Add fill layer for indigenous reserves
    map.addLayer({
      id: 'indigenous-reserves-fill',
      type: 'fill',
      source: 'indigenous-reserves',
      'source-layer': 'indigenous_reserves',
      paint: {
        'fill-color': '#00CED1',
        'fill-opacity': 0.5
      }
    });

    // Add border layer for indigenous reserves
    map.addLayer({
      id: 'indigenous-reserves-borders',
      type: 'line',
      source: 'indigenous-reserves',
      'source-layer': 'indigenous_reserves',
      paint: {
        'line-color': '#008B8B',
        'line-width': 2
      }
    });

    // Add click event to show popup with indigenous reserve name
    map.on('click', 'indigenous-reserves-fill', (e) => {
      const features = map.queryRenderedFeatures(e.point, {
        layers: ['indigenous-reserves-fill']
      });

      if (features.length > 0) {
        const feature = features[0];
        const name = feature.properties.name || 'Resguardo Indígena';
        const pueblo = feature.properties.pueblo || '';

        // Create popup content
        const popupContent = pueblo 
          ? `<strong>${name}</strong><br/><span style="font-size: 11px; color: #666;">${pueblo}</span>`
          : `<strong>${name}</strong>`;

        // Show popup
        new maplibregl.Popup()
          .setLngLat(e.lngLat)
          .setHTML(popupContent)
          .addTo(map);

        // Highlight the selected feature by adding a temporary highlight layer
        // First, remove any existing highlight
        if (map.getLayer('indigenous-reserves-highlight')) {
          map.removeLayer('indigenous-reserves-highlight');
        }

        // Add highlight layer
        map.addLayer({
          id: 'indigenous-reserves-highlight',
          type: 'line',
          source: 'indigenous-reserves',
          'source-layer': 'indigenous_reserves',
          filter: ['==', 'name', name],
          paint: {
            'line-color': '#FFFFFF',
            'line-width': 4,
            'line-dasharray': [2, 1]
          }
        });
      }
    });

    // Change cursor on hover
    map.on('mouseenter', 'indigenous-reserves-fill', () => {
      map.getCanvas().style.cursor = 'pointer';
    });

    map.on('mouseleave', 'indigenous-reserves-fill', () => {
      map.getCanvas().style.cursor = '';
    });

    // Log source loading events
    map.on('source.load', 'indigenous-reserves', () => {
      console.log('✅ Indigenous reserves source loaded successfully');
    });

    map.on('source.error', 'indigenous-reserves', (e) => {
      console.error('❌ Indigenous reserves source error:', e.error);
    });
    
    console.log('Indigenous reserves vector tiles added to map');
  };

  const addCommunityCouncilsToMap = (map) => {
    console.log('Adding community councils as vector tiles');
    
    // Remove existing source if present
    if (map.getSource('community-councils')) {
      if (map.getLayer('community-councils-highlight')) {
        map.removeLayer('community-councils-highlight');
      }
      map.removeLayer('community-councils-fill');
      map.removeLayer('community-councils-borders');
      map.removeSource('community-councils');
    }

    const mvtUrlTemplate = CommunityCouncilService.getMvtUrlTemplate();
    console.log('Community Councils MVT URL Template:', mvtUrlTemplate);
    
    // Add vector tile source
    map.addSource('community-councils', {
      type: 'vector',
      tiles: [mvtUrlTemplate],
      minzoom: 0,
      maxzoom: 18
    });

    // Add fill layer for community councils
    map.addLayer({
      id: 'community-councils-fill',
      type: 'fill',
      source: 'community-councils',
      'source-layer': 'community_councils',
      paint: {
        'fill-color': '#FFD700',
        'fill-opacity': 0.25
      }
    });

    // Add border layer for community councils
    map.addLayer({
      id: 'community-councils-borders',
      type: 'line',
      source: 'community-councils',
      'source-layer': 'community_councils',
      paint: {
        'line-color': '#FF8C00',
        'line-width': 2
      }
    });

    // Add click event to show popup with community council name
    map.on('click', 'community-councils-fill', (e) => {
      const features = map.queryRenderedFeatures(e.point, {
        layers: ['community-councils-fill']
      });

      if (features.length > 0) {
        const feature = features[0];
        const name = feature.properties.name || 'Consejo Comunitario';

        // Show popup
        new maplibregl.Popup()
          .setLngLat(e.lngLat)
          .setHTML(`<strong>${name}</strong>`)
          .addTo(map);

        // Highlight the selected feature
        if (map.getLayer('community-councils-highlight')) {
          map.removeLayer('community-councils-highlight');
        }

        map.addLayer({
          id: 'community-councils-highlight',
          type: 'line',
          source: 'community-councils',
          'source-layer': 'community_councils',
          filter: ['==', 'name', name],
          paint: {
            'line-color': '#FFFFFF',
            'line-width': 4,
            'line-dasharray': [2, 1]
          }
        });
      }
    });

    // Change cursor on hover
    map.on('mouseenter', 'community-councils-fill', () => {
      map.getCanvas().style.cursor = 'pointer';
    });

    map.on('mouseleave', 'community-councils-fill', () => {
      map.getCanvas().style.cursor = '';
    });

    // Log source loading events
    map.on('source.load', 'community-councils', () => {
      console.log('✅ Community councils source loaded successfully');
    });

    map.on('source.error', 'community-councils', (e) => {
      console.error('❌ Community councils source error:', e.error);
    });
    
    console.log('Community councils vector tiles added to map');
  };

  const addTerritoriosCampesinosAgroalimentariosToMap = (map) => {
    console.log('Adding territorios campesinos agroalimentarios as vector tiles');
    
    // Remove existing source if present
    if (map.getSource('territorios-campesinos-agroalimentarios')) {
      map.removeLayer('territorios-campesinos-agroalimentarios-fill');
      map.removeLayer('territorios-campesinos-agroalimentarios-borders');
      map.removeSource('territorios-campesinos-agroalimentarios');
    }

    const mvtUrlTemplate = TerritorioCampesinoAgroalimentarioService.getMvtUrlTemplate();
    console.log('Territorios Campesinos Agroalimentarios MVT URL Template:', mvtUrlTemplate);
    
    // Add vector tile source
    map.addSource('territorios-campesinos-agroalimentarios', {
      type: 'vector',
      tiles: [mvtUrlTemplate],
      minzoom: 0,
      maxzoom: 18
    });

    // Add fill layer
    map.addLayer({
      id: 'territorios-campesinos-agroalimentarios-fill',
      type: 'fill',
      source: 'territorios-campesinos-agroalimentarios',
      'source-layer': 'territorios_campesinos_agroalimentarios',
      paint: {
        'fill-color': '#9370DB',
        'fill-opacity': 0.25
      }
    });

    // Add border layer
    map.addLayer({
      id: 'territorios-campesinos-agroalimentarios-borders',
      type: 'line',
      source: 'territorios-campesinos-agroalimentarios',
      'source-layer': 'territorios_campesinos_agroalimentarios',
      paint: {
        'line-color': '#8A2BE2',
        'line-width': 2
      }
    });

    // Log source loading events
    map.on('source.load', 'territorios-campesinos-agroalimentarios', () => {
      console.log('✅ Territorios campesinos agroalimentarios source loaded successfully');
    });

    map.on('source.error', 'territorios-campesinos-agroalimentarios', (e) => {
      console.error('❌ Territorios campesinos agroalimentarios source error:', e.error);
    });
    
    console.log('Territorios campesinos agroalimentarios vector tiles added to map');
  };

  const addRunapToMap = (map) => {
    console.log('Adding RUNAP areas as vector tiles');
    
    // Remove existing source if present
    if (map.getSource('runap')) {
      if (map.getLayer('runap-highlight')) {
        map.removeLayer('runap-highlight');
      }
      map.removeLayer('runap-fill');
      map.removeLayer('runap-borders');
      map.removeSource('runap');
    }

    const mvtUrlTemplate = RunapService.getMvtUrlTemplate();
    console.log('RUNAP MVT URL Template:', mvtUrlTemplate);
    
    // Add vector tile source
    map.addSource('runap', {
      type: 'vector',
      tiles: [mvtUrlTemplate],
      minzoom: 0,
      maxzoom: 18
    });

    // Add fill layer
    map.addLayer({
      id: 'runap-fill',
      type: 'fill',
      source: 'runap',
      'source-layer': 'runap',
      paint: {
        'fill-color': '#90EE90',
        'fill-opacity': 0.25
      }
    });

    // Add border layer
    map.addLayer({
      id: 'runap-borders',
      type: 'line',
      source: 'runap',
      'source-layer': 'runap',
      paint: {
        'line-color': '#228B22',
        'line-width': 2
      }
    });

    // Add click event to show popup with RUNAP name
    map.on('click', 'runap-fill', (e) => {
      const features = map.queryRenderedFeatures(e.point, {
        layers: ['runap-fill']
      });

      if (features.length > 0) {
        const feature = features[0];
        const name = feature.properties.ap_nombre || 'Área Protegida';

        // Show popup
        new maplibregl.Popup()
          .setLngLat(e.lngLat)
          .setHTML(`<strong>${name}</strong>`)
          .addTo(map);

        // Highlight the selected feature
        if (map.getLayer('runap-highlight')) {
          map.removeLayer('runap-highlight');
        }

        map.addLayer({
          id: 'runap-highlight',
          type: 'line',
          source: 'runap',
          'source-layer': 'runap',
          filter: ['==', 'ap_nombre', name],
          paint: {
            'line-color': '#FFFFFF',
            'line-width': 4,
            'line-dasharray': [2, 1]
          }
        });
      }
    });

    // Change cursor on hover
    map.on('mouseenter', 'runap-fill', () => {
      map.getCanvas().style.cursor = 'pointer';
    });

    map.on('mouseleave', 'runap-fill', () => {
      map.getCanvas().style.cursor = '';
    });

    // Log source loading events
    map.on('source.load', 'runap', () => {
      console.log('✅ RUNAP source loaded successfully');
    });

    map.on('source.error', 'runap', (e) => {
      console.error('❌ RUNAP source error:', e.error);
    });
    
    console.log('RUNAP vector tiles added to map');
  };

  const addTituloVigenteToMap = (map) => {
    console.log('Adding Titulos Vigentes as vector tiles');
    
    // Remove existing source if present
    if (map.getSource('titulo-vigente')) {
      if (map.getLayer('titulo-vigente-highlight')) {
        map.removeLayer('titulo-vigente-highlight');
      }
      map.removeLayer('titulo-vigente-fill');
      map.removeLayer('titulo-vigente-borders');
      map.removeSource('titulo-vigente');
    }

    const mvtUrlTemplate = TituloVigenteService.getMvtUrlTemplate();
    console.log('Titulos Vigentes MVT URL Template:', mvtUrlTemplate);
    
    // Add vector tile source
    map.addSource('titulo-vigente', {
      type: 'vector',
      tiles: [mvtUrlTemplate],
      minzoom: 0,
      maxzoom: 18
    });

    // Add fill layer
    map.addLayer({
      id: 'titulo-vigente-fill',
      type: 'fill',
      source: 'titulo-vigente',
      'source-layer': 'titulo_vigente',
      paint: {
        'fill-color': '#FF0000',
        'fill-opacity': 0.25
      }
    });

    // Add border layer
    map.addLayer({
      id: 'titulo-vigente-borders',
      type: 'line',
      source: 'titulo-vigente',
      'source-layer': 'titulo_vigente',
      paint: {
        'line-color': '#FF0000',
        'line-width': 2
      }
    });

    // Add click event to show popup with titulo vigente details
    map.on('click', 'titulo-vigente-fill', (e) => {
      const features = map.queryRenderedFeatures(e.point, {
        layers: ['titulo-vigente-fill']
      });

      if (features.length > 0) {
        const feature = features[0];
        const props = feature.properties;
        
        // Build popup content with most important fields
        let popupContent = `<strong>${props.codigo_exp || 'Título Minero'}</strong>`;
        
        if (props.titulo_est) {
          popupContent += `<br/><span style="color: #666;">Estado: ${props.titulo_est}</span>`;
        }
        if (props.minerales) {
          popupContent += `<br/><span style="color: #666;">Minerales: ${props.minerales}</span>`;
        }
        if (props.area_ha) {
          popupContent += `<br/><span style="color: #666;">Área: ${props.area_ha.toLocaleString()} ha</span>`;
        }
        if (props.departamen) {
          popupContent += `<br/><span style="color: #666;">Departamento: ${props.departamen}</span>`;
        }
        if (props.municipios) {
          popupContent += `<br/><span style="color: #666;">Municipios: ${props.municipios}</span>`;
        }

        // Show popup
        new maplibregl.Popup()
          .setLngLat(e.lngLat)
          .setHTML(popupContent)
          .addTo(map);

        // Highlight the selected feature
        if (map.getLayer('titulo-vigente-highlight')) {
          map.removeLayer('titulo-vigente-highlight');
        }

        map.addLayer({
          id: 'titulo-vigente-highlight',
          type: 'line',
          source: 'titulo-vigente',
          'source-layer': 'titulo_vigente',
          filter: ['==', 'codigo_exp', props.codigo_exp],
          paint: {
            'line-color': '#FFFFFF',
            'line-width': 4,
            'line-dasharray': [2, 1]
          }
        });
      }
    });

    // Change cursor on hover
    map.on('mouseenter', 'titulo-vigente-fill', () => {
      map.getCanvas().style.cursor = 'pointer';
    });

    map.on('mouseleave', 'titulo-vigente-fill', () => {
      map.getCanvas().style.cursor = '';
    });

    // Log source loading events
    map.on('source.load', 'titulo-vigente', () => {
      console.log('✅ Titulos Vigentes source loaded successfully');
    });

    map.on('source.error', 'titulo-vigente', (e) => {
      console.error('❌ Titulos Vigentes source error:', e.error);
    });
    
    console.log('Titulos Vigentes vector tiles added to map');
  };

  const addZonaReservaCampesinaToMap = (map) => {
    console.log('Adding Zonas de Reserva Campesina as vector tiles');
    
    // Remove existing source if present
    if (map.getSource('zona-reserva-campesina')) {
      if (map.getLayer('zona-reserva-campesina-highlight')) {
        map.removeLayer('zona-reserva-campesina-highlight');
      }
      map.removeLayer('zona-reserva-campesina-fill');
      map.removeLayer('zona-reserva-campesina-borders');
      map.removeSource('zona-reserva-campesina');
    }

    const mvtUrlTemplate = ZonaReservaCampesinaService.getMvtUrlTemplate();
    console.log('Zonas de Reserva Campesina MVT URL Template:', mvtUrlTemplate);
    
    // Add vector tile source
    map.addSource('zona-reserva-campesina', {
      type: 'vector',
      tiles: [mvtUrlTemplate],
      minzoom: 0,
      maxzoom: 18
    });

    // Add fill layer
    map.addLayer({
      id: 'zona-reserva-campesina-fill',
      type: 'fill',
      source: 'zona-reserva-campesina',
      'source-layer': 'zona_reserva_campesina',
      paint: {
        'fill-color': '#DAA520',
        'fill-opacity': 0.25
      }
    });

    // Add border layer
    map.addLayer({
      id: 'zona-reserva-campesina-borders',
      type: 'line',
      source: 'zona-reserva-campesina',
      'source-layer': 'zona_reserva_campesina',
      paint: {
        'line-color': '#B8860B',
        'line-width': 2
      }
    });

    // Add click event to show popup with zona name
    map.on('click', 'zona-reserva-campesina-fill', (e) => {
      const features = map.queryRenderedFeatures(e.point, {
        layers: ['zona-reserva-campesina-fill']
      });

      if (features.length > 0) {
        const feature = features[0];
        const name = feature.properties.nombre_zon || 'Zona de Reserva Campesina';

        // Show popup
        new maplibregl.Popup()
          .setLngLat(e.lngLat)
          .setHTML(`<strong>${name}</strong>`)
          .addTo(map);

        // Highlight the selected feature
        if (map.getLayer('zona-reserva-campesina-highlight')) {
          map.removeLayer('zona-reserva-campesina-highlight');
        }

        map.addLayer({
          id: 'zona-reserva-campesina-highlight',
          type: 'line',
          source: 'zona-reserva-campesina',
          'source-layer': 'zona_reserva_campesina',
          filter: ['==', 'nombre_zon', name],
          paint: {
            'line-color': '#FFFFFF',
            'line-width': 4,
            'line-dasharray': [2, 1]
          }
        });
      }
    });

    // Change cursor on hover
    map.on('mouseenter', 'zona-reserva-campesina-fill', () => {
      map.getCanvas().style.cursor = 'pointer';
    });

    map.on('mouseleave', 'zona-reserva-campesina-fill', () => {
      map.getCanvas().style.cursor = '';
    });

    // Log source loading events
    map.on('source.load', 'zona-reserva-campesina', () => {
      console.log('✅ Zonas de Reserva Campesina source loaded successfully');
    });

    map.on('source.error', 'zona-reserva-campesina', (e) => {
      console.error('❌ Zonas de Reserva Campesina source error:', e.error);
    });
    
    console.log('Zonas de Reserva Campesina vector tiles added to map');
  };

  const addIdeamDeforestationToMap = (map) => {
    console.log('Adding IDEAM Deforestation WMS layer to map');
    
    // Remove existing layer if present
    if (map.getLayer('ideam-deforestation-fill')) {
      map.removeLayer('ideam-deforestation-fill');
    }
    if (map.getSource('ideam-deforestation')) {
      map.removeSource('ideam-deforestation');
    }
    
    // Add WMS source and layer
    map.addSource('ideam-deforestation', {
      type: 'raster',
      tiles: [
        'https://visualizador.ideam.gov.co/gisserver/services/Dinamica_Cambio_Cobertura_Bosque/MapServer/WMSServer?version=1.3.0&service=WMS&request=GetMap&layers=0&styles=&crs=EPSG:3857&bbox={bbox-epsg-3857}&width=256&height=256&format=image/png'
      ],
      tileSize: 256,
      attribution: '© IDEAM - Instituto de Hidrología, Meteorología y Estudios Ambientales'
    });
    
    map.addLayer({
      id: 'ideam-deforestation-fill',
      type: 'raster',
      source: 'ideam-deforestation',
      paint: {
        'raster-opacity': 0.7,
        'raster-contrast': 0.2,
        'raster-saturation': -0.2
      }
    });
    
    console.log('IDEAM Deforestation layer added to map');
  };

  const fitMapToFilteredFeatures = () => {
    const map = mapRef.current;
    if (!map) return;

    // Check if layers exist
    if (!map.getLayer('projects-fill')) {
      return;
    }

    try {
      // Get all features from the projects layers
      const fillFeatures = map.queryRenderedFeatures({ layers: ['projects-fill'] });
      
      if (fillFeatures.length === 0) return;

      // Calculate bounding box from features
      let minLng = Infinity, maxLng = -Infinity;
      let minLat = Infinity, maxLat = -Infinity;

      fillFeatures.forEach(feature => {
        const geometry = feature.geometry;
        if (!geometry) return;
        
        // Handle different geometry types
        if (geometry.type === 'Polygon') {
          // Polygon: coordinates[0] is the outer ring
          const rings = geometry.coordinates;
          rings.forEach(ring => {
            ring.forEach(coord => {
              if (Array.isArray(coord) && coord.length >= 2) {
                const [lng, lat] = coord;
                minLng = Math.min(minLng, lng);
                maxLng = Math.max(maxLng, lng);
                minLat = Math.min(minLat, lat);
                maxLat = Math.max(maxLat, lat);
              }
            });
          });
        } else if (geometry.type === 'MultiPolygon') {
          // MultiPolygon: coordinates[polygonIndex][ringIndex][coordIndex]
          const polygons = geometry.coordinates;
          polygons.forEach(polygon => {
            polygon.forEach(ring => {
              ring.forEach(coord => {
                if (Array.isArray(coord) && coord.length >= 2) {
                  const [lng, lat] = coord;
                  minLng = Math.min(minLng, lng);
                  maxLng = Math.max(maxLng, lng);
                  minLat = Math.min(minLat, lat);
                  maxLat = Math.max(maxLat, lat);
                }
              });
            });
          });
        } else if (geometry.type === 'Point') {
          const coord = geometry.coordinates;
          if (Array.isArray(coord) && coord.length >= 2) {
            const [lng, lat] = coord;
            minLng = Math.min(minLng, lng);
            maxLng = Math.max(maxLng, lng);
            minLat = Math.min(minLat, lat);
            maxLat = Math.max(maxLat, lat);
          }
        }
      });

      // Check if we have valid bounds
      if (minLng === Infinity || maxLng === -Infinity) return;

      // Add padding
      const padding = 0.1;
      minLng -= padding;
      minLat -= padding;
      maxLng += padding;
      maxLat += padding;

      map.fitBounds([[minLng, minLat], [maxLng, maxLat]], {
        padding: 50,
        maxZoom: 12,
        duration: 1000
      });
    } catch (error) {
      console.error('Error fitting map:', error);
    }
  };

  const applyFiltersToMap = () => {
    const map = mapRef.current;
    if (!map) {
      console.log('applyFiltersToMap: map is null');
      return;
    }

    if (!map.getLayer('projects-fill')) {
      console.log('applyFiltersToMap: project layers not ready');
      return;
    }

    console.log('Applying filters:', filters);

    // Reset map to default view (all of Colombia) before applying filter
    // This ensures all tiles are loaded so the filter can work on all data
    const currentZoom = map.getZoom();
    if (currentZoom > 5) {
      console.log('Resetting map to default view before filtering');
      map.flyTo({
        center: [-74, 4],
        zoom: 5,
        duration: 1000
      });
      
      // Wait for animation to complete before applying filter and zoom
      setTimeout(() => {
        applyFilterAndZoom(map);
      }, 1500);
      return;
    }
    
    // If already at default zoom, apply filter and zoom directly
    applyFilterAndZoom(map);
  };

  const applyFilterAndZoom = (map) => {
    let filterConditions = [];

    if (filters.departamento) {
      // Use 'in' to check if the department contains the selected value
      filterConditions.push(['in', filters.departamento, ['get', 'departamento']]);
    }

    if (filters.status) {
      filterConditions.push(['==', ['get', 'status'], filters.status]);
    }

    if (filters.certifier) {
      filterConditions.push(['==', ['get', 'certifier'], filters.certifier]);
    }

    // Filter by project name (exact match)
    if (filters.name) {
      filterConditions.push(['==', ['get', 'name'], filters.name]);
    }

    const layerFilter = filterConditions.length > 0 ? ['all', ...filterConditions] : null;
    console.log('Applying layer filter:', layerFilter);

    if (map.getLayer('projects-fill')) {
      map.setFilter('projects-fill', layerFilter);
    }
    if (map.getLayer('projects-borders')) {
      map.setFilter('projects-borders', layerFilter);
    }

    // Auto-zoom to filtered features after a delay to let tiles load
    setTimeout(() => {
      fitMapToFilteredFeatures();
    }, 500);
  };

  useEffect(() => {
    if (loading || !mapContainer.current || mapRef.current) return;

    console.log('Creating map...');

    try {
      const map = new maplibregl.Map({
        container: mapContainer.current,
        style: 'https://api.maptiler.com/maps/base-v4/style.json?key=hZhsSP8BKg1S4yfT2ssZ',
        center: [-74, 4],
        zoom: 5
      });

      mapRef.current = map;

      console.log('Map created successfully');

      map.addControl(new maplibregl.GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true
        },
        trackUserLocation: true
      }), 'bottom-right');

      map.on('load', () => {
        console.log('Map loaded');
        setMapLoaded(true);
        // Remove the hardcoded country and department layers since they're now in the base style
      });

      return () => map.remove();
    } catch (error) {
      console.error('Error creating map:', error);
    }
  }, [loading]);

  useEffect(() => {
    if (mapRef.current && mapLoaded && allProjects.length > 0) {
      const map = mapRef.current;
      const handleClick = (e) => {
        const features = map.queryRenderedFeatures(e.point, { layers: ['projects-fill'] });
        if (features.length > 0) {
          const projectId = features[0].properties.id;
          const project = allProjects.find(p => p.id == projectId);
          setSelectedProject(project);
        }
      };
      map.on('click', 'projects-fill', handleClick);
      return () => {
        map.off('click', 'projects-fill', handleClick);
      };
    }
  }, [mapLoaded, allProjects]);

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '16px' }}>
        <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem', color: '#007bff' }}></i>
        <span style={{ fontSize: '1.1rem', color: '#666' }}>Cargando mapa...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '16px', padding: '20px' }}>
        <i className="pi pi-exclamation-triangle" style={{ fontSize: '3rem', color: '#dc3545' }}></i>
        <h3 style={{ color: '#dc3545', margin: 0 }}>Error al cargar el mapa</h3>
        <p style={{ color: '#666', margin: '8px 0 0 0' }}>Por favor, verifica tu conexión e intenta nuevamente.</p>
      </div>
    );
  }

  return (
    <div style={{
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflowX: 'hidden'
    }}>
      {/* Filters Section - Collapsible */}
      <div 
        className="border-bottom-1 border-200 shadow-2"
        style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          borderBottom: '1px solid #e9ecef',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.12)',
          transition: 'all 0.3s ease-in-out',
          position: 'relative',
          flexShrink: 0
        }}
      >
        {/* Collapsible Header */}
        <div 
          onClick={() => setIsFiltersCollapsed(!isFiltersCollapsed)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '8px',
            cursor: 'pointer',
            background: '#f8f9fa',
            borderBottom: isFiltersCollapsed ? 'none' : '1px solid #e9ecef'
          }}
        >
          <span style={{ fontWeight: 600, color: '#495057', marginRight: '8px' }}>Filtros</span>
          <i 
            className={`pi ${isFiltersCollapsed ? 'pi-chevron-down' : 'pi-chevron-up'}`} 
            style={{ color: '#6c757d' }}
          ></i>
        </div>
        
        {/* Filters Content */}
        <div style={{
          padding: isFiltersCollapsed ? '0' : '16px',
          maxHeight: isFiltersCollapsed ? '0' : '500px',
          overflow: 'hidden',
          transition: 'all 0.3s ease-in-out'
        }}>
        {/* Subtle decorative element */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: 'linear-gradient(90deg, #007bff 0%, #28a745 50%, #6f42c1 100%)',
          opacity: 0.7
        }}></div>
        <div className="container-fluid">
          <div className="flex flex-column md:flex-row gap-4 align-items-start md:align-items-end justify-content-between">
            <div className="flex flex-wrap gap-4 align-items-end">
              <div className="flex flex-column">
                <label htmlFor="name-filter" className="text-sm font-medium mb-1">
                  <i className="pi pi-search mr-1"></i>Nombre del Proyecto
                </label>
              <Dropdown
                id="name-filter"
                value={filters.name}
                options={projectNames}
                onChange={(e) => setFilters({ ...filters, name: e.value })}
                placeholder="Todos los proyectos"
                showClear
                filter
                filterPlaceholder="Buscar proyecto..."
                className="w-full"
                style={{ minWidth: '250px' }}
              />
            </div>

            <div className="flex flex-column">
              <label htmlFor="departamento-filter" className="text-sm font-medium mb-1">
                <i className="pi pi-map-marker mr-1"></i>Departamento
              </label>
              <Dropdown
                id="departamento-filter"
                value={filters.departamento}
                options={departamentos}
                onChange={(e) => setFilters({ ...filters, departamento: e.value })}
                placeholder="Todos los departamentos"
                showClear
                filter
                className="w-full"
                style={{ minWidth: '200px' }}
              />
            </div>

            <div className="flex flex-column">
              <label htmlFor="status-filter" className="text-sm font-medium mb-1">
                <i className="pi pi-info-circle mr-1"></i>Estado
              </label>
              <Dropdown
                id="status-filter"
                value={filters.status}
                options={statuses}
                onChange={(e) => setFilters({ ...filters, status: e.value })}
                placeholder="Todos los estados"
                showClear
                filter
                className="w-full"
                style={{ minWidth: '200px' }}
              />
            </div>

            <div className="flex flex-column">
              <label htmlFor="certifier-filter" className="text-sm font-medium mb-1">
                <i className="pi pi-verified mr-1"></i>Certificador
              </label>
              <Dropdown
                id="certifier-filter"
                value={filters.certifier}
                options={certifiers}
                onChange={(e) => setFilters({ ...filters, certifier: e.value })}
                placeholder="Todos los certificadores"
                showClear
                filter
                className="w-full"
                style={{ minWidth: '200px' }}
              />
            </div>
            </div>
            
            {/* Project Count Display */}
            <div className="flex align-items-center justify-content-center p-3 border-1 border-round surface-100" style={{ minWidth: '180px' }}>
              <i className="pi pi-map-marker text-primary mr-2"></i>
              <span className="font-medium">
                {filteredProjectCount} proyecto{filteredProjectCount !== 1 ? 's' : ''} visible{filteredProjectCount !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Map Section */}
      <div style={{
        position: 'relative',
        display: 'flex',
        transition: 'all 0.3s ease-in-out',
        height: '100%'
      }}>
        {selectedProject && (
          <div style={{
            width: '480px',
            flexShrink: 0,
            overflowY: 'auto',
            background: 'rgba(255, 255, 255, 0.98)',
            borderRight: '1px solid #e9ecef',
            boxShadow: '2px 0 8px rgba(0,0,0,0.1)',
            zIndex: 10
          }}>
            <ProjectDetailsPanel project={selectedProject} onClose={() => setSelectedProject(null)} />
          </div>
        )}
        <div
          ref={mapContainer}
          style={{
            flex: 1,
            height: '100%',
            transition: 'all 0.3s ease-in-out',
            minHeight: '400px'
          }}
        />

        {/* Floating Layer Control Panel */}
        <div style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 1000,
          width: isLayersPanelExpanded ? '250px' : '40px',
          transition: 'width 0.3s ease-in-out',
          overflow: 'hidden',
          whiteSpace: 'nowrap'
        }}>
          {/* Toggle Button */}
          <div 
            onClick={() => setIsLayersPanelExpanded(!isLayersPanelExpanded)}
            style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              padding: '8px 4px',
              borderBottom: isLayersPanelExpanded ? '1px solid #e9ecef' : 'none',
              marginBottom: isLayersPanelExpanded ? '8px' : '0'
            }}
          >
            <span style={{ 
              fontSize: '18px', 
              color: '#666',
              marginRight: '8px',
              transition: 'transform 0.3s ease-in-out',
              transform: isLayersPanelExpanded ? 'rotate(90deg)' : 'rotate(0deg)'
            }}>
              ▶
            </span>
            {isLayersPanelExpanded && (
              <h6 style={{ margin: 0, fontSize: '14px', fontWeight: 'bold' }}>Capas</h6>
            )}
          </div>
          {isLayersPanelExpanded && (
            <div style={{ padding: '0 4px' }}>
              {layerLoading && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  background: '#f8f9fa',
                  borderRadius: '4px',
                  marginBottom: '12px',
                  fontSize: '12px',
                  color: '#666'
                }}>
                  <i className="pi pi-spin pi-spinner" style={{ fontSize: '14px' }}></i>
                  <span>Loading {layerLoading.replace(/([A-Z])/g, ' $1').trim()}...</span>
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    inputId="departments-layer"
                    checked={layerVisibility.departments}
                    onChange={(e) => handleDepartmentsLayerToggle(e.checked)}
                  />
                  <label htmlFor="departments-layer" style={{ marginLeft: '8px', fontSize: '13px', flex: 1 }}>
                    Departamentos
                  </label>
                  <div style={{ width: '12px', height: '12px', backgroundColor: '#888888', borderRadius: '2px' }}></div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    inputId="indigenous-reserves-layer"
                    checked={layerVisibility.indigenousReserves}
                    onChange={(e) => handleIndigenousReservesLayerToggle(e.checked)}
                  />
                  <label htmlFor="indigenous-reserves-layer" style={{ marginLeft: '8px', fontSize: '13px', flex: 1 }}>
                    Resguardos Indígenas
                  </label>
                  <div style={{ width: '12px', height: '12px', backgroundColor: '#00CED1', borderRadius: '2px' }}></div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    inputId="community-councils-layer"
                    checked={layerVisibility.communityCouncils}
                    onChange={(e) => handleCommunityCouncilsLayerToggle(e.checked)}
                  />
                  <label htmlFor="community-councils-layer" style={{ marginLeft: '8px', fontSize: '13px', flex: 1 }}>
                    Consejos Comunitarios
                  </label>
                  <div style={{ width: '12px', height: '12px', backgroundColor: '#FFA500', borderRadius: '2px' }}></div>
                </div>
                {/* Territorios Campesinos Agroalimentarios layer hidden */}
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    inputId="runap-layer"
                    checked={layerVisibility.runap}
                    onChange={(e) => handleRunapLayerToggle(e.checked)}
                  />
                  <label htmlFor="runap-layer" style={{ marginLeft: '8px', fontSize: '13px', flex: 1 }}>
                    RUNAP (Áreas Protegidas)
                  </label>
                  <div style={{ width: '12px', height: '12px', backgroundColor: '#90EE90', borderRadius: '2px' }}></div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    inputId="titulo-vigente-layer"
                    checked={layerVisibility.tituloVigente}
                    onChange={(e) => handleTituloVigenteLayerToggle(e.checked)}
                  />
                  <label htmlFor="titulo-vigente-layer" style={{ marginLeft: '8px', fontSize: '13px', flex: 1 }}>
                    Títulos Vigentes (Minería)
                  </label>
                  <div style={{ width: '12px', height: '12px', backgroundColor: '#FF0000', borderRadius: '2px' }}></div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    inputId="zona-reserva-campesina-layer"
                    checked={layerVisibility.zonaReservaCampesina}
                    onChange={(e) => handleZonaReservaCampesinaLayerToggle(e.checked)}
                  />
                  <label htmlFor="zona-reserva-campesina-layer" style={{ marginLeft: '8px', fontSize: '13px', flex: 1 }}>
                    Zonas de Reserva Campesina
                  </label>
                  <div style={{ width: '12px', height: '12px', backgroundColor: '#FFD700', borderRadius: '2px' }}></div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    inputId="ideam-deforestation-layer"
                    checked={layerVisibility.ideamDeforestation}
                    onChange={(e) => handleIdeamDeforestationLayerToggle(e.checked)}
                  />
                  <label htmlFor="ideam-deforestation-layer" style={{ marginLeft: '8px', fontSize: '13px', flex: 1 }}>
                    IDEAM - Deforestación (2025)
                  </label>
                  <div style={{ width: '12px', height: '12px', backgroundColor: '#318335', borderRadius: '2px' }}></div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MapComponent;